import { AppBar, Tab, Tabs, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import {
  AutocompleteArrayInput,
  BooleanInput,
  DateInput,
  DateTimeInput,
  Edit,
  Link,
  SaveButton,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  required,
  useEditController,
  useNotify,
  useQuery,
  useRefresh,
} from 'react-admin';
import Modal from 'react-modal';
import { useLocation, useParams } from 'react-router-dom';
import { customDataProvider } from '../data-provider';
import { UserReviews } from '../reviews/user-reviews';
import { UserTips } from '../tips/user-tips';
import {
  NoTitle,
  encodeJSONToBase64,
  getFormattedDate,
  getFormattedDate_T,
  getMembershipActionName,
  getMembershipTypeName,
  getPlanId,
  getUtcToLocalDateTime,
  isAdmin,
  isDayBirthday,
  isFiveDigitZip,
  isFoundingMember,
  isGss,
  isSuperAdmin,
  onlyLetters,
  showToast,
} from '../utils';
import {
  FRANCHISE_PHASES,
  GUESTS,
  HEARD_ABOUT_US,
  MEMBERSHIP_CANCEL_REASON,
  MEMBERSHIP_STATUS,
  MEMBERSHIP_TYPES,
  STATES,
} from '../utils/constants';
import { UserCancelledAppointment } from './user-cancelled-appointments';
import { UserCompletedAppointment } from './user-completed-appointments';
import { UserTransactionsHistory } from './user-transactions-history';
import { UserUpcomingAppointment } from './user-upcoming-appointments';
import { UserTransactions } from './user-wallet-transactions';
import './user.css';
import styles from '../gift/gift.module.css';
import config from '../app.config';
// import { useForm } from 'react-final-form';

const dangerBtn = {
  color: '#fff',
  backgroundColor: 'red',
};

const a11yProps = index => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
};

const validateAmount = [required()];
const validateDate = [required()];
const validateReason = [required()];
const validateOther = [required()];
const validateMembership = [required()];
const validateFirstName = [required(), onlyLetters];
const validateLastName = [required(), onlyLetters];
const validateAddress = [required()];
const validateState = [required()];
const validateCity = [required(), onlyLetters];
const validateZipCode = [required(), isFiveDigitZip];

const getPauseFeeRate = membershipRecord => {
  const configured = Number(membershipRecord?.pause_fee_rate);
  if (configured > 0) {
    return configured;
  }
  const current = Number(membershipRecord?.pause_fee);
  if (current > 0) {
    return current;
  }
  return 15;
};

const isEndingMembership = membershipRecord =>
  _.isEmpty(membershipRecord) ||
  [3, 4, 5].includes(membershipRecord?.entity_status_id) ||
  !!membershipRecord?.cancel_requested_at ||
  !!membershipRecord?.cancel_effective_at ||
  !!membershipRecord?.cancelled_at;

const isOnPaidPauseFee = membershipRecord => {
  if (
    !membershipRecord?.paused_at ||
    !membershipRecord?.next_due_at ||
    membershipRecord?.pause_fee_waived
  ) {
    return false;
  }
  return (
    membershipRecord.membership_terms_version === 'v02' ||
    !!membershipRecord.pause_fee_plan
  );
};

const getPauseBillingLabel = membershipRecord => {
  if (isEndingMembership(membershipRecord)) {
    return '';
  }
  if (membershipRecord?.pause_fee_waived) {
    return 'Waived';
  }
  if (isOnPaidPauseFee(membershipRecord)) {
    return `$${getPauseFeeRate(membershipRecord)}/mo`;
  }
  if (membershipRecord?.paused_at) {
    return 'Unpaid pause';
  }
  return 'Not paused';
};

const PAUSE_DURATION_CHOICES = Array.from({ length: 12 }, (_, index) => {
  const months = index + 1;
  return {
    id: months,
    name: months === 1 ? '1 Month' : `${months} Months`,
  };
});

const getPauseResumeAt = (pauseMonths, fromDate = new Date()) => {
  const resumeAt = new Date(fromDate);
  resumeAt.setMonth(resumeAt.getMonth() + (Number(pauseMonths) || 1));
  return resumeAt;
};

const formatPauseResumeAtForApi = pauseMonths => {
  const resumeAt = getPauseResumeAt(pauseMonths);
  const pad = value => String(value).padStart(2, '0');
  return `${resumeAt.getFullYear()}-${pad(resumeAt.getMonth() + 1)}-${pad(
    resumeAt.getDate(),
  )} ${pad(resumeAt.getHours())}:${pad(resumeAt.getMinutes())}:${pad(
    resumeAt.getSeconds(),
  )}`;
};

const getDefaultPauseDurationMonths = membershipRecord => {
  const resumeAtValue = membershipRecord?.resume_at;
  if (!resumeAtValue) {
    return 1;
  }
  const resumeAt = new Date(String(resumeAtValue).replace(/-/g, '/'));
  if (Number.isNaN(resumeAt.getTime())) {
    return 1;
  }
  const months = Math.round(
    (resumeAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44),
  );
  if (months < 1) {
    return 1;
  }
  if (months > 12) {
    return 12;
  }
  return months;
};

const validatePauseMonths = [required()];

const toLocalDateInputValue = utcValue => {
  if (!utcValue) {
    return '';
  }
  const local = moment.utc(utcValue).local();
  if (!local.isValid()) {
    return '';
  }
  return local.format('YYYY-MM-DD');
};

const selectedCalendarDate = selectedDate => {
  if (!selectedDate) {
    return null;
  }
  if (
    typeof selectedDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
  ) {
    const parsed = moment(selectedDate, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }
  const parsed = moment(selectedDate);
  return parsed.isValid() ? parsed : null;
};

// Keep the current charge's time of day and move the calendar day the guest sees.
const formatBillingDateForApi = (selectedDate, currentDueAt) => {
  const selected = selectedCalendarDate(selectedDate);
  const currentLocal = moment.utc(currentDueAt).local();
  if (!selected || !currentLocal.isValid()) {
    return null;
  }
  return currentLocal
    .clone()
    .year(selected.year())
    .month(selected.month())
    .date(selected.date())
    .utc()
    .format('YYYY-MM-DD HH:mm:ss');
};

export const UserEdit = ({ permissions, ...props }) => {
  const user_id = props.id;
  const [appointmentIndex, setAppointmentIndex] = React.useState(0);
  const [transactionsIndex, setTransactionsIndex] = React.useState(0);
  const [showModal, setShowModal] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditResumeDateModal, setShowEditResumeDateModal] = useState(false);
  const [reason, setReason] = useState('');
  const [transactionType, setTransactionType] = useState('appointment');
  const [_heardAbout, _setHeardAbout] = useState('');
  const refresh = useRefresh();
  const notify = useNotify();
  const { id } = useParams();
  const location = useLocation();
  // Trusted admins can unlock Home Location Change via ?show=change on the guest URL.
  const showChangeHomeLocationParam =
    new URLSearchParams(location.search).get('show') === 'change';
  const [showChangeHomeLocationModal, setShowChangeHomeLocationModal] =
    useState(false);
  const [showChangeMebershipTypeModal, setShowChangeMebershipTypeModal] =
    useState(false);
  const [showChangeMembershipTermsModal, setShowChangeMembershipTermsModal] =
    useState(false);
  const [selectedTermsVersion, setSelectedTermsVersion] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenCreating, setIsTokenCreating] = useState(false);
  const [isMembershipTokenCreating, setIsMembershipTokenCreating] =
    useState(false);
  const [showQuickCancelModal, setShowQuickCancelModal] = useState(false);
  const [showPauseFeeModal, setShowPauseFeeModal] = useState(false);
  const [pauseFeeMonths, setPauseFeeMonths] = useState(1);
  const [showChangeBillingDateModal, setShowChangeBillingDateModal] =
    useState(false);

  // WithPermissions often provides permissions after the first paint; authProvider
  // uses the same role string from localStorage, so fall back for stable checks.
  const resolvedRole =
    permissions ||
    (typeof window !== 'undefined' ? localStorage.getItem('role') : null);

  const handleChange = (event, newValue) => {
    setAppointmentIndex(newValue);
  };

  const workLocations = JSON.parse(localStorage.getItem('userWorkLocations'));

  const handleTransTabChange = (event, newValue) => {
    setTransactionsIndex(newValue);
  };
  const { data: locations } = useQuery({
    type: 'getList',
    resource: 'locations',
    payload: {},
  });

  const { data: membership } = useQuery({
    type: 'getOne',
    resource: 'membership',
    payload: {
      user_id,
    },
  });

  const { data: legalInfo } = useQuery({
    type: 'getOne',
    resource: 'legalInfo',
    payload: { userId: user_id },
  });

  const controllerProps = useEditController({
    basePath: `/guest/${props.id}`,
    permissions: 'admin',
    resource: 'guest',
    id: props.id,
  });
  const user = controllerProps.record;

  const handleCancelMembership = form => {
    const otherReason = form?.cancelReason?.includes('Other');
    if (otherReason) {
      form?.cancelReason.push(form?.otherReason);
    }
    const reasons = form?.cancelReason?.filter(a => a !== 'Other');
    const payload = {
      user_id: Number(props?.id),
      action: 'cancel',
      cancel_reason: reasons?.join(', '),
    };
    customDataProvider
      .update('cancelMembership', {
        ...payload,
      })
      .then(
        () => {
          setShowCancelModal(false);
          notify('Membership cancelled!');
          refresh();
        },
        err => {
          notify(
            err.response ? err.response?.data?.message : err.message,
            'error',
          );
          refresh();
        },
      );
  };

  const handleQuickCancelMembership = form => {
    const otherReason = form?.cancelReason?.includes('Other');
    if (otherReason) {
      form?.cancelReason.push(form?.otherReason);
    }
    const reasons = form?.cancelReason?.filter(a => a !== 'Other');
    const payload = {
      user_id: Number(props?.id),
      action: 'quick_cancel',
      cancel_reason: reasons?.join(', ') || 'Quick cancel',
    };
    customDataProvider
      .update('cancelMembership', {
        ...payload,
      })
      .then(
        () => {
          setShowQuickCancelModal(false);
          setCancelReason('');
          notify('Membership quick-cancelled — benefits ended immediately.');
          refresh();
        },
        err => {
          notify(
            err.response ? err.response?.data?.message : err.message,
            'error',
          );
          refresh();
        },
      );
  };

  const handleEditResumeDateMembership = form => {
    customDataProvider
      .update('editMembershipResumeDate', {
        membership_id: membership?.id,
        resume_date: form?.resume_at,
      })
      .then(
        () => {
          setShowEditResumeDateModal(false);
          notify('Membership Resume Pause updated!');
          refresh();
        },
        err => {
          notify(
            err.response ? err.response?.data?.message : err.message,
            'error',
          );
          refresh();
        },
      );
  };

  const handleChangeBillingDate = form => {
    const nextDueAt = formatBillingDateForApi(
      form?.next_due_at,
      membership?.next_due_at,
    );
    if (!nextDueAt) {
      notify('Choose a billing date', 'warning');
      return;
    }
    return customDataProvider
      .update('membershipBillingDate', {
        user_id: Number(props?.id),
        next_due_at: nextDueAt,
      })
      .then(
        () => {
          setShowChangeBillingDateModal(false);
          notify('Membership renewal date updated.');
          refresh();
        },
        err => {
          notify(
            err.response ? err.response?.data?.message : err.message,
            'error',
          );
          refresh();
        },
      );
  };

  const handleEnrollPauseFee = form => {
    const pauseMonths = form?.pauseMonths || pauseFeeMonths || 1;
    const payload = {
      user_id: Number(props?.id),
      action: 'pause_fee',
      resume_at: formatPauseResumeAtForApi(pauseMonths),
    };
    customDataProvider.update('pauseFeePlan', payload).then(
      () => {
        setShowPauseFeeModal(false);
        notify('Guest is on the pause fee plan.');
        refresh();
      },
      err => {
        notify(
          err.response ? err.response?.data?.message : err.message,
          'error',
        );
        refresh();
      },
    );
  };

  const TransactionTypes = [
    { id: 'appointment', name: 'Appointments' },
    { id: 'membership', name: 'Membership' },
    { id: 'gift', name: 'Gift' },
    { id: 'tips', name: 'Tips' },
    { id: 'addons', name: 'Addons' },
    { id: 'package', name: 'Packages' },
  ];

  const handleWalletUpdate = () => {
    setShowModal(true);
    refresh();
  };

  const showHomeLocation = () => {
    const location_id = user?.location_id
      ? user.location_id
      : membership.location_id;
    if (!locations) {
      return '';
    }
    const home_location = locations.find(loc => loc.id === location_id);

    return home_location && `${home_location.name}`;
  };

  const handleChangeHomeLocation = () => {
    setShowChangeHomeLocationModal(true);
  };

  const handleUpdateHomeLocation = form => {
    const payload = { location_id: form?.location, id: user?.id };
    customDataProvider.update('user', payload).then(
      () => {
        setShowChangeHomeLocationModal(false);
        notify('Home Location updated!');
        refresh();
      },
      err => {
        notify(
          err.response ? err.response?.data?.message : err.message,
          'error',
        );
      },
    );
  };

  const getLocationsOptions = () => {
    const optionsLocations = [];
    workLocations.map(loc => {
      return (
        loc.location.launch_status !== FRANCHISE_PHASES.PHASE3 &&
        optionsLocations.push({
          id: loc.location_id,
          label: `${loc.location.name}`,
          name: `${loc.location.name}`,
        })
      );
    });
    const filterLocations = _.orderBy(optionsLocations, ['state'], ['asc']);
    return filterLocations;
  };

  const ChangeLocationToolbar = props => (
    <Toolbar {...props}>
      <SaveButton label="Save" onSave={handleUpdateHomeLocation} />
    </Toolbar>
  );

  const changeHomeLocationModal = () => {
    return (
      <Modal isOpen={showChangeHomeLocationModal}>
        <div className="modal-header">
          <div className="light-text"> Change Home Location </div>
          <CloseIcon
            onClick={() => setShowChangeHomeLocationModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">Choose Home Location</div>
        <SimpleForm
          // save={handleUpdateHomeLocation}
          toolbar={
            <ChangeLocationToolbar
              handleUpdateHomeLocation={handleUpdateHomeLocation}
            />
          }
          initialValues={{ location: membership?.location_id }}
        >
          <SelectInput source="location" choices={getLocationsOptions()} />
        </SimpleForm>
      </Modal>
    );
  };

  const handleMembershipTypeUpdate = formValues => {
    const { membership_type_id, stripe_plan_id } = membership;
    const { membershipTypes } = formValues;
    const isFounding = membership_type_id === 1 ? '' : 'founding';
    if (membershipTypes === stripe_plan_id + isFounding) {
      notify(
        `Already On '${getMembershipTypeName(membershipTypes)}' Membership`,
      );
      return;
    }

    const isFoundingOptionSelected = membershipTypes.includes('founding');
    const stripePlanSelected = membershipTypes.split('_')[0];
    const payload = {
      userId: user?.id,
      planId: membershipTypes.split('_')[0],
      action: getMembershipActionName(membershipTypes),
    };

    if (stripePlanSelected !== stripe_plan_id) {
      updateMembershipPlan(payload);
    }
    setTimeout(() => {
      if (!(isFoundingMember === isFoundingMember(membership))) {
        toggleFoundingMembership(
          {
            applyFoundingMembership: isFoundingOptionSelected,
            membershipId: membership?.id,
          },
          membershipTypes,
        );
      }
    }, 1000);
  };

  const toggleFoundingMembership = (payload, membershipTypes) => {
    customDataProvider.update('membershipFouding', payload).then(
      () => {
        setShowChangeMebershipTypeModal(false);
        showToast(
          `Updated to 
              ${getMembershipTypeName(membershipTypes)} Membership!`,
          'success',
        );
        refresh();
      },
      err => {
        setShowChangeMebershipTypeModal(false);
        notify(err.message, 'error');
      },
    );
  };

  const updateMembershipPlan = payload => {
    customDataProvider.update('membershipPlan', payload).then(
      () => {
        setShowChangeMebershipTypeModal(false);
        refresh();
      },
      err => {
        setShowChangeMebershipTypeModal(false);
        notify(err.message, 'error');
      },
    );
  };

  const handleMembershipTermsUpdate = formValues => {
    const nextVersion = formValues.membership_terms_version;
    const currentVersion = membership?.membership_terms_version;
    if (!nextVersion) {
      notify('Select a membership terms version', 'warning');
      return;
    }
    if (nextVersion === currentVersion) {
      notify(`Already on ${currentVersion} terms`);
      return;
    }
    customDataProvider
      .update('membershipTerms', {
        userId: user?.id || Number(props?.id),
        membership_terms_version: nextVersion,
      })
      .then(
        () => {
          setShowChangeMembershipTermsModal(false);
          setSelectedTermsVersion(null);
          showToast(`Updated membership terms to ${nextVersion}`, 'success');
          refresh();
        },
        err => {
          setShowChangeMembershipTermsModal(false);
          notify(
            err.response ? err.response?.data?.message : err.message,
            'error',
          );
        },
      );
  };

  const EditMembershipToolbar = props => (
    <Toolbar
      {...props}
      style={{ display: 'flex', justifyContent: 'space-around' }}
    >
      <Button
        style={dangerBtn}
        variant="outlined"
        color="danger"
        onClick={() => setShowChangeMebershipTypeModal(false)}
      >
        Cancel
      </Button>
      <SaveButton label="Save" onSave={handleMembershipTypeUpdate} />
    </Toolbar>
  );

  const EditMembershipTermsToolbar = props => (
    <Toolbar
      {...props}
      style={{ display: 'flex', justifyContent: 'space-around' }}
    >
      <Button
        style={dangerBtn}
        variant="outlined"
        color="danger"
        onClick={() => {
          setShowChangeMembershipTermsModal(false);
          setSelectedTermsVersion(null);
        }}
      >
        Cancel
      </Button>
      <SaveButton label="Save" onSave={handleMembershipTermsUpdate} />
    </Toolbar>
  );

  const getTermsPreviewNote = () => {
    const version =
      selectedTermsVersion || membership?.membership_terms_version;
    const nextDue = membership?.next_due_at
      ? getUtcToLocalDateTime(membership.next_due_at, false)
      : '—';
    // Prefer API terms_pricing; fall back to membership_type.price for v01
    // (and when previewing the guest's current terms version).
    let monthlyRate = membership?.terms_pricing?.[version];
    if (
      (monthlyRate == null || monthlyRate === '') &&
      membership?.price != null &&
      (version === 'v01' ||
        version === membership?.membership_terms_version)
    ) {
      monthlyRate = membership.price;
    }
    const rateLabel =
      monthlyRate != null && monthlyRate !== ''
        ? `$${Number(monthlyRate)}`
        : 'the selected terms rate';
    return `Note: The next bill date will be ${nextDue} at ${rateLabel} per month`;
  };

  const changeMebershipTypeModal = () => {
    return (
      <Modal isOpen={showChangeMebershipTypeModal}>
        <div className="modal-header">
          <div className="light-text">Edit Membership</div>
          <CloseIcon
            onClick={() => {
              setShowChangeMebershipTypeModal(false);
            }}
            className="close-button"
          />
        </div>
        <div className="text-center font-medium mt-3">
          Current Membership Type:{' '}
          {!isFoundingMember(membership) ? 'Regular' : 'Founding'} -{' '}
          {membership?.stripe_plan_id === '80minv2' ? 'Main' : 'Mid'}
        </div>
        <Edit {...props} className="simpleform-details-display">
          <SimpleForm toolbar={<EditMembershipToolbar />}>
            <SelectInput
              validate={validateMembership}
              source="membershipTypes"
              label="Change Membership Type"
              choices={MEMBERSHIP_TYPES}
            />
          </SimpleForm>
        </Edit>
      </Modal>
    );
  };

  const changeMembershipTermsModal = () => {
    const currentVersion = membership?.membership_terms_version || 'v02';
    const otherVersion = currentVersion === 'v01' ? 'v02' : 'v01';
    return (
      <Modal isOpen={showChangeMembershipTermsModal}>
        <div className="modal-header">
          <div className="light-text">Edit Membership Terms</div>
          <CloseIcon
            onClick={() => {
              setShowChangeMembershipTermsModal(false);
              setSelectedTermsVersion(null);
            }}
            className="close-button"
          />
        </div>
        <div className="text-center font-medium mt-3">
          Current Membership: {currentVersion}
        </div>
        <Edit {...props} className="simpleform-details-display">
          <SimpleForm
            toolbar={<EditMembershipTermsToolbar />}
            initialValues={{ membership_terms_version: otherVersion }}
          >
            <SelectInput
              validate={validateMembership}
              source="membership_terms_version"
              label="Change Membership Terms"
              choices={[
                { id: 'v01', name: 'Change to v01 Terms' },
                { id: 'v02', name: 'Change to v02 Terms' },
              ]}
              onChange={event => {
                const value = event?.target?.value ?? event;
                setSelectedTermsVersion(value);
              }}
            />
          </SimpleForm>
        </Edit>
        <div className="btn-padding text-center">{getTermsPreviewNote()}</div>
      </Modal>
    );
  };

  const handleSave = form => {
    setIsSubmitting(true);
    const payload = {
      amount: form.amount,
      reason: form.reason,
      otherReason: form.otherReason,
    };
    customDataProvider.update('wallet', { id: props?.id, payload }).then(
      () => {
        setShowModal(false);
        notify('Wallet updated!');
        refresh();
        setIsSubmitting(false);
      },
      err => {
        notify(
          err.response ? err.response?.data?.message : err.message,
          'error',
        );
        refresh();
        setIsSubmitting(false);
      },
    );
  };

  const walletModal = () => {
    return (
      <Modal isOpen={showModal}>
        <div className="modal-header">
          <div className="light-text">Adjust Squeeze Bucks</div>
          <CloseIcon
            onClick={() => setShowModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">
          Enter the amount you want to add or subtract (+/-) and the reason.
        </div>
        {isSubmitting ? (
          <div className={styles.loader}></div>
        ) : (
          <SimpleForm
            save={handleSave}
            initialValues={{ reason: 'Guest Satisfaction' }}
          >
            <TextInput source="amount" validate={validateAmount} />
            <SelectInput
              source="reason"
              onChange={e => setReason(e.target.value)}
              choices={[
                { id: 'Guest Satisfaction', name: 'Guest Satisfaction' },
                { id: 'Promotional Credit', name: 'Promotional Credit' },
                { id: 'Other', name: 'Other' },
              ]}
            />
            {reason === 'Other' && <TextInput source="otherReason" />}
          </SimpleForm>
        )}
      </Modal>
    );
  };

  const CancelMembershipToolbar = props => (
    <Toolbar {...props}>
      <SaveButton label="CANCEL MEMBERSHIP" onSave={handleCancelMembership} />
    </Toolbar>
  );

  const QuickCancelMembershipToolbar = props => (
    <Toolbar {...props}>
      <SaveButton
        label="QUICK CANCEL NOW"
        onSave={handleQuickCancelMembership}
      />
    </Toolbar>
  );

  const cancelModal = () => {
    return (
      <Modal isOpen={showCancelModal}>
        <div className="modal-header">
          <div className="light-text">Cancel Membership</div>
          <CloseIcon
            onClick={() => {
              setShowCancelModal(false);
              setCancelReason('');
            }}
            className="close-button"
          />
        </div>
        <div className="cancelModal">
          <SimpleForm
            // save={handleCancelMembership}
            toolbar={<CancelMembershipToolbar />}
            initialValues={{ cancelReason: '' }}
          >
            <p className="w100">
              Note, the guest's membership will remain active until the end of
              the current membership term,{' '}
              {getFormattedDate(membership?.next_due_at)}. Please choose the
              reasons why the guest is choosing to cancel:
            </p>

            <AutocompleteArrayInput
              source="cancelReason"
              validate={validateReason}
              onChange={e => setCancelReason(e)}
              choices={MEMBERSHIP_CANCEL_REASON}
            />
            {cancelReason?.includes('Other') && (
              <TextInput
                label="Other Reason"
                source="otherReason"
                validate={validateOther}
              />
            )}
          </SimpleForm>
        </div>
      </Modal>
    );
  };

  const quickCancelModal = () => {
    return (
      <Modal isOpen={showQuickCancelModal}>
        <div className="modal-header">
          <div className="light-text">Quick Cancel Membership</div>
          <CloseIcon
            onClick={() => {
              setShowQuickCancelModal(false);
              setCancelReason('');
            }}
            className="close-button"
          />
        </div>
        <div className="cancelModal">
          <SimpleForm
            toolbar={<QuickCancelMembershipToolbar />}
            initialValues={{ cancelReason: '' }}
          >
            <p className="w100">
              <strong>Super-admin only.</strong> This ends membership benefits
              immediately today — it does not wait for the next billing date
              {membership?.cancel_effective_at || membership?.next_due_at
                ? ` (${getFormattedDate(
                    membership?.cancel_effective_at || membership?.next_due_at,
                  )})`
                : ''}
              . Member pricing and Squeeze Bucks accrual from this membership
              stop right away. Choose a reason:
            </p>

            <AutocompleteArrayInput
              source="cancelReason"
              validate={validateReason}
              onChange={e => setCancelReason(e)}
              choices={MEMBERSHIP_CANCEL_REASON}
            />
            {cancelReason?.includes('Other') && (
              <TextInput
                label="Other Reason"
                source="otherReason"
                validate={validateOther}
              />
            )}
          </SimpleForm>
        </div>
      </Modal>
    );
  };
  const changeBillingDateModal = () => {
    const currentLabel = membership?.next_due_at
      ? getUtcToLocalDateTime(membership.next_due_at, false)
      : '';
    return (
      <Modal isOpen={showChangeBillingDateModal}>
        <div className="modal-header">
          <div className="light-text">Change Membership Renewal Date</div>
          <CloseIcon
            onClick={() => setShowChangeBillingDateModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">
          Current renewal date: {currentLabel}. The next charge moves to the
          date you choose, and later charges bill on that day each month. Pick
          a future date within 62 days of the current renewal date.
        </div>
        {showChangeBillingDateModal && (
          <SimpleForm
            save={handleChangeBillingDate}
            initialValues={{
              next_due_at: toLocalDateInputValue(membership?.next_due_at),
            }}
          >
            <DateInput
              source="next_due_at"
              label="Next billing date"
              validate={validateDate}
            />
          </SimpleForm>
        )}
      </Modal>
    );
  };

  const editResumeDateModal = () => {
    return (
      <Modal isOpen={showEditResumeDateModal}>
        <div className="modal-header">
          <div className="light-text">Resume Pause Date</div>
          <CloseIcon
            onClick={() => setShowEditResumeDateModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">Select Date</div>
        <SimpleForm
          save={handleEditResumeDateMembership}
          initialValues={{ resume_at: membership?.resume_at }}
        >
          <DateTimeInput
            source="resume_at"
            defaultValue={membership?.resume_at}
            label="Start Date"
            validate={validateDate}
          />
        </SimpleForm>
      </Modal>
    );
  };

  const PauseFeeToolbar = toolbarProps => (
    <Toolbar
      {...toolbarProps}
      style={{ display: 'flex', justifyContent: 'center' }}
    >
      <SaveButton
        label="SAVE"
        onSave={handleEnrollPauseFee}
        disabled={toolbarProps.invalid || toolbarProps.saving}
      />
    </Toolbar>
  );

  const pauseFeeModal = () => {
    const pauseFeeRate = getPauseFeeRate(membership);
    const pauseFeeWaived = !!(
      membership?.pause_fee_waived || user?.pause_fee_waived
    );
    const schedulingNewPause =
      membership?.entity_status_id === 1 && !membership?.paused_at;
    const termsVersion = membership?.membership_terms_version || 'v01';
    const nextDue = membership?.next_due_at
      ? getUtcToLocalDateTime(membership.next_due_at, false)
      : null;
    const actionLabel = schedulingNewPause
      ? `Pause for $${pauseFeeRate}/mo`
      : `Move to $${pauseFeeRate} pause fee`;
    const resumeOn = getFormattedDate(getPauseResumeAt(pauseFeeMonths));

    return (
      <Modal isOpen={showPauseFeeModal}>
        <div className="modal-header">
          <div className="light-text">{actionLabel}</div>
          <CloseIcon
            onClick={() => setShowPauseFeeModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">
          {pauseFeeWaived ? (
            <p>
              This guest has the monthly pause fee waived. Turn that off and
              save the guest before enrolling them in the ${pauseFeeRate}/mo
              pause fee.
            </p>
          ) : (
            <>
              <p>
                {schedulingNewPause
                  ? `Pause this ${termsVersion} membership on the $${pauseFeeRate}/mo pause fee. The pause starts on ${
                      nextDue || 'the next membership date'
                    }, and the pause fee is billed from that date until they resume. Membership terms stay ${termsVersion}. They keep member rates and do not earn new Squeeze Bucks while paused.`
                  : `Move this ${termsVersion} unpaid pause onto the $${pauseFeeRate}/mo pause fee. The first pause fee is billed on the next pause-fee run. Membership terms stay ${termsVersion}. They keep member rates and do not earn new Squeeze Bucks while paused.`}
              </p>
              {showPauseFeeModal && (
                <SimpleForm
                  toolbar={<PauseFeeToolbar />}
                  initialValues={{ pauseMonths: pauseFeeMonths }}
                >
                  <SelectInput
                    source="pauseMonths"
                    label="Pause for"
                    choices={PAUSE_DURATION_CHOICES}
                    validate={validatePauseMonths}
                    onChange={event => {
                      const value = Number(event?.target?.value ?? event);
                      setPauseFeeMonths(value || 1);
                    }}
                  />
                  <div className="btn-padding">
                    Your membership will resume on: {resumeOn}
                  </div>
                </SimpleForm>
              )}
            </>
          )}
        </div>
      </Modal>
    );
  };

  const getMembershipStatus = membership => {
    if (_.isEmpty(membership)) {
      return 'non-member';
    } else if (membership?.entity_status_id === 3) {
      return 'Cancelled';
    } else if (
      membership?.cancel_requested_at ||
      membership?.cancel_effective_at ||
      membership?.cancelled_at
    ) {
      // Prefer cancel_effective_at; fall back to legacy cancelled_at / next_due_at.
      const termEnd =
        membership?.cancel_effective_at ||
        membership?.cancelled_at ||
        membership?.next_due_at;
      const endDate = getUtcToLocalDateTime(termEnd, false);
      return endDate ? `To be Cancelled (on ${endDate})` : 'To be Cancelled';
    } else if (membership?.paused_at && membership?.entity_status_id === 1) {
      // Pause is scheduled; membership stays active until next_due_at.
      const pauseDate = getUtcToLocalDateTime(membership?.next_due_at, false);
      return pauseDate ? `To be Paused (on ${pauseDate})` : 'To be Paused';
    } else if (membership?.paused_at) {
      return 'Paused';
    } else if (membership?.campaign && membership?.campaign_id) {
      return `F${_.capitalize(membership?.name)} (${getPlanId(
        membership?.stripe_plan_id,
      )})`;
    } else {
      return `${_.capitalize(membership?.name)} (${getPlanId(
        membership?.stripe_plan_id,
      )})`;
    }
  };

  const handleUserUpdate = formValues => {
    const {
      first_name,
      last_name,
      phone,
      dob,
      guest_notes,
      vi_squeeze,
      heardAboutOther,
      heardAbout,
      yelp_reviewer,
      google_reviewer,
      pause_fee_waived,
    } = formValues;
    let payload = {
      id: user_id,
      first_name,
      last_name: last_name || '',
      phone: phone || '',
      dob: dob || '',
      guest_notes: guest_notes || '',
      vi_squeeze: vi_squeeze ? true : false,
      yelp_reviewer: yelp_reviewer ? true : false,
      google_reviewer: google_reviewer ? true : false,
      pause_fee_waived: pause_fee_waived ? true : false,
    };
    if (heardAbout)
      payload.heard_about_us =
        heardAbout === 'Other' ? heardAboutOther : heardAbout;
    customDataProvider.update('user', payload).then(
      () => {
        notify('User updated!');
      },
      err => {
        notify(
          err.response ? err.response?.data?.message : err.message,
          'error',
        );
        refresh();
      },
    );
  };

  const handleLegalInfoUpdate = formValues => {
    const { legal_first_name, legal_last_name, state, city, address, zipcode } =
      formValues;
    const payload = {
      user_id,
      legal_first_name,
      legal_last_name,
      state,
      city,
      address,
      zipcode: String(zipcode),
      hasLegalInfo: !isEmpty(legalInfo),
    };

    customDataProvider.update('legalInfo', payload).then(
      () => {
        notify('Legal Info Updated!');
        refresh();
      },
      err => {
        notify(
          err.response ? err.response?.data?.message : err.message,
          'error',
        );
      },
    );
  };
  const EditToolbar = props => (
    <Toolbar {...props}>
      <SaveButton label="Save" onSave={handleUserUpdate} />
    </Toolbar>
  );

  const LegalInfoEditToolbar = props => (
    <Toolbar {...props}>
      <SaveButton label="Save" onSave={handleLegalInfoUpdate} />
    </Toolbar>
  );
  const iconWrap = icon => (
    <span className="icon-wrapper">
      <div class="fc-title-icon icon-fontsize w-10">{icon}</div>
    </span>
  );

  const preferenceBtn = {
    backgroundColor: '#2196F3',
    width: '6rem',
    borderRadius: '0.25rem',
    padding: '0.5rem',
  };

  const openImpersonatedFlow = (encodedData, flow) => {
    try {
      const redirectUrl = new URL(config.redirectUrl);
      redirectUrl.searchParams.set('data', encodedData);
      if (flow) {
        redirectUrl.searchParams.set('flow', flow);
      }
      window.open(redirectUrl.toString(), '_blank');
    } catch (error) {
      const flowQuery = flow ? `&flow=${flow}` : '';
      window.open(
        `${config.redirectUrl}?data=${encodeURIComponent(encodedData)}${flowQuery}`,
        '_blank',
      );
    }
  };

  const handleApptBooking = () => {
    if (user_id) {
      setIsTokenCreating(true);
      customDataProvider
        .create('createUserToken', {
          guestId: user_id,
        })
        .then(data => {
          if (data?.resp?.token) {
            const adminUser = JSON.parse(localStorage.getItem('user'));
            const adminUserDetails = {
              adminId: adminUser.id,
              adminEmail: adminUser.email,
            };

            const encodedData = encodeJSONToBase64({
              ...data?.resp,
              adminUserDetails,
            });
            openImpersonatedFlow(encodedData);
            setIsTokenCreating(false);
          }
        })
        .catch(() => setIsTokenCreating(false));
    }
  };

  const handleMembershipPurchase = () => {
    if (user_id) {
      setIsMembershipTokenCreating(true);
      customDataProvider
        .create('createUserToken', {
          guestId: user_id,
        })
        .then(data => {
          if (data?.resp?.token) {
            const adminUser = JSON.parse(localStorage.getItem('user'));
            const adminUserDetails = {
              adminId: adminUser.id,
              adminEmail: adminUser.email,
            };

            const encodedData = encodeJSONToBase64({
              ...data?.resp,
              adminUserDetails,
            });
            openImpersonatedFlow(encodedData, 'membership');
            setIsMembershipTokenCreating(false);
          }
        })
        .catch(() => setIsMembershipTokenCreating(false));
    }
  };
  const renderIcons = () => {
    return (
      <div>
        {user && user.has_membership ? iconWrap('star_sharp') : ''}
        {user && user.minor ? iconWrap('escalator_warning') : ''}
        {user && user.vi_squeeze ? iconWrap('emoji_events_sharp') : ''}
        {user && user.pregnant ? iconWrap('local_hospital') : ''}
        {user && user.dob && isDayBirthday(user.dob) ? iconWrap('cake') : ''}
        {user && user.first_time_user ? iconWrap('fiber_new_sharp') : ''}
      </div>
    );
  };

  //   const FormErrors = () => {
  //     const form = useForm();
  //     const values = form.getState().values;
  //     const [formValues, setFormValues] = useState(values);

  //     useEffect(() => {
  //       const unsubscribe = form.subscribe(
  //         ({ values }) => {
  //           setFormValues(values);
  //         },
  //         { values: true }
  //       );
  //       return () => unsubscribe();
  //     }, [form]);

  //     const zipcodeValidation = validateZipCode[1]?.(formValues?.zipcode);

  //     const errors = {
  //       firstName: validateFirstName[1]?.(formValues?.legal_first_name)?.message,
  //       lastName: validateLastName[1]?.(values?.legal_last_name)?.message,
  //       city: validateCity[1]?.(values?.city)?.message,
  //       zipcode: formValues?.zipcode ? zipcodeValidation : undefined,
  //     };
  // console.log({errors},'KKKKKKKKK');

  //     return errors;
  //   };

  return (
    <>
      {changeMebershipTypeModal()}
      {changeMembershipTermsModal()}
      {walletModal()}
      {cancelModal()}
      {quickCancelModal()}
      {editResumeDateModal()}
      {changeBillingDateModal()}
      {pauseFeeModal()}
      {changeHomeLocationModal()}
      <div className="details-display">
        <Edit
          resource={'guest'}
          basePath={`/guest`}
          id={id}
          initialValues={{
            heard_about_us: 'Select',
          }}
          title="Edit User"
          undoable={false}
          {...props}
        >
          <SimpleForm
            className="simpleform-details-display"
            toolbar={<EditToolbar />}
          >
            <TextInput source="email" disabled={!isSuperAdmin(resolvedRole)} />
            <TextInput source="first_name" />
            <TextInput source="last_name" />
            <TextInput source="phone" />
            <DateInput source="dob" label="Date of Birth" />
            <SelectInput
              label="Heard about us?"
              source="heardAbout"
              choices={HEARD_ABOUT_US}
              initialValue={'Select'}
              onChange={e => _setHeardAbout(e.target.value)}
            />
            {(user?.heardAbout === 'Other' || _heardAbout === 'Other') && (
              <TextInput label="Other" source="heardAboutOther" multiline />
            )}
            <div className="flex w-0-imp">
              <BooleanInput
                label="V.I. Squeeze?"
                options={{
                  id: 'isViSqueeze',
                  // checked: test,
                }}
                source="vi_squeeze"
              />
              <BooleanInput
                label="Yelp Reviewer?"
                options={{
                  id: 'isYelpReviewer',
                  // checked: test,
                }}
                source="yelp_reviewer"
              />
              <BooleanInput
                label="Google Reviewer?"
                options={{
                  id: 'isGoogleReviewer',
                  // checked: test,
                }}
                source="google_reviewer"
              />
              <BooleanInput
                label="Waive monthly pause fee?"
                options={{
                  id: 'isPauseFeeWaived',
                }}
                source="pause_fee_waived"
              />
            </div>
          </SimpleForm>
        </Edit>
        <div className="flex-icons">
          <div className="big-icon-wrapper text-align-right flex-column">
            <div>{renderIcons()}</div>
            <div className="book-a-massage guest-impersonate-actions">
              <button
                className={
                  isTokenCreating
                    ? 'has-flex button-blue disabled:opacity-50 hover:bg-blue-700'
                    : 'button-blue'
                }
                onClick={() => handleApptBooking()}
                disabled={isTokenCreating || isMembershipTokenCreating}
              >
                {isTokenCreating && (
                  <div class="loader ease-linear rounded-full border-2 border-t-2 border-gray-200 h-4 w-4 mr-3"></div>
                )}
                BOOK A MASSAGE
              </button>
              {!user?.has_membership && (
                <button
                  className={
                    isMembershipTokenCreating
                      ? 'has-flex button-blue disabled:opacity-50 hover:bg-blue-700'
                      : 'button-blue'
                  }
                  onClick={() => handleMembershipPurchase()}
                  disabled={isTokenCreating || isMembershipTokenCreating}
                >
                  {isMembershipTokenCreating && (
                    <div class="loader ease-linear rounded-full border-2 border-t-2 border-gray-200 h-4 w-4 mr-3"></div>
                  )}
                  PURCHASE MEMBERSHIP
                </button>
              )}
            </div>
          </div>

          <Show title={<NoTitle />} {...props} actions="">
            <SimpleShowLayout className="right-panel">
              <div className="pt-2">
                <div className="label">Liability Waiver?</div>
                <div>{!!user?.liability_waiver ? 'Yes' : 'No'}</div>
                <div className="label">Last Visit</div>
                <div>
                  {user?.last_appointment_date
                    ? getFormattedDate_T(user?.last_appointment_date)
                    : ''}
                </div>
                <div className="label">
                  <div className="details-display">
                    <div>Membership?</div>
                    {(isSuperAdmin(resolvedRole) ||
                      isAdmin(resolvedRole) ||
                      isGss(resolvedRole)) &&
                      !_.isEmpty(membership) &&
                      membership?.entity_status_id === 1 && (
                        <div
                          className="blueLink text-center"
                          onClick={() => setShowChangeMebershipTypeModal(true)}
                        >
                          Change
                        </div>
                      )}
                  </div>
                </div>
                <div>{getMembershipStatus(membership)}</div>
                {!_.isEmpty(membership) &&
                  membership?.membership_terms_version && (
                    <>
                      <div className="label">
                        <div className="details-display">
                          <div>Membership terms</div>
                          {(isSuperAdmin(resolvedRole) ||
                            isAdmin(resolvedRole) ||
                            isGss(resolvedRole)) &&
                            membership?.entity_status_id === 1 && (
                              <div
                                className="blueLink text-center"
                                onClick={() => {
                                  const current =
                                    membership?.membership_terms_version ||
                                    'v02';
                                  setSelectedTermsVersion(
                                    current === 'v01' ? 'v02' : 'v01',
                                  );
                                  setShowChangeMembershipTermsModal(true);
                                }}
                              >
                                Change
                              </div>
                            )}
                        </div>
                      </div>
                      <div>{membership.membership_terms_version}</div>
                    </>
                  )}
                {!_.isEmpty(membership) && !isEndingMembership(membership) && (
                  <>
                    <div className="label">Pause fee</div>
                    <div>
                      <div>{getPauseBillingLabel(membership)}</div>
                      {(isSuperAdmin(resolvedRole) ||
                        isAdmin(resolvedRole) ||
                        isGss(resolvedRole)) &&
                        !isOnPaidPauseFee(membership) && (
                          <div
                            className="blueLink"
                            onClick={() => {
                              setPauseFeeMonths(
                                getDefaultPauseDurationMonths(membership),
                              );
                              setShowPauseFeeModal(true);
                            }}
                          >
                            {membership?.entity_status_id === 1 &&
                            !membership?.paused_at
                              ? `Pause for $${getPauseFeeRate(membership)}/mo`
                              : `Move to $${getPauseFeeRate(membership)} pause fee`}
                          </div>
                        )}
                    </div>
                  </>
                )}
                {!(
                  _.isEmpty(membership) ||
                  membership?.cancel_requested_at ||
                  membership?.cancel_effective_at ||
                  membership?.cancelled_at ||
                  membership?.entity_status_id === 3
                ) && (
                  <>
                    <div className="label">
                      <div className="details-display">
                        <div>
                          {membership?.paused_at
                            ? 'Resume Pause Date'
                            : 'Membership Renewal Date'}
                        </div>
                        {membership?.paused_at && membership?.resume_at && (
                          <div
                            className="blueLink text-center"
                            onClick={() => setShowEditResumeDateModal(true)}
                          >
                            Edit
                          </div>
                        )}
                        {!membership?.paused_at &&
                          membership?.next_due_at &&
                          membership?.entity_status_id === 1 &&
                          (isSuperAdmin(resolvedRole) ||
                            isAdmin(resolvedRole) ||
                            isGss(resolvedRole)) && (
                            <div
                              className="blueLink text-center"
                              onClick={() =>
                                setShowChangeBillingDateModal(true)
                              }
                            >
                              Change
                            </div>
                          )}
                      </div>
                    </div>
                    <div>
                      {membership
                        ? getUtcToLocalDateTime(
                            membership?.paused_at
                              ? membership?.resume_at
                              : membership?.next_due_at,
                            // membership?.location?.timezone,
                            false,
                          )
                        : ''}
                    </div>
                  </>
                )}
                {!_.isEmpty(membership) &&
                  membership?.entity_status_id !== 3 &&
                  membership?.status !== 'cancelled' &&
                  (!(
                    membership?.cancel_requested_at ||
                    membership?.cancel_effective_at ||
                    membership?.cancelled_at
                  ) ||
                    isSuperAdmin(resolvedRole)) && (
                    <>
                      <div className="label">Cancel Membership</div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {!(
                          membership?.cancel_requested_at ||
                          membership?.cancel_effective_at ||
                          membership?.cancelled_at
                        ) && (
                          <div
                            className="blueLink"
                            onClick={() => setShowCancelModal(true)}
                          >
                            Cancel
                          </div>
                        )}
                        {isSuperAdmin(resolvedRole) && (
                          <div
                            className="blueLink"
                            onClick={() => setShowQuickCancelModal(true)}
                          >
                            Quick Cancel
                          </div>
                        )}
                      </div>
                    </>
                  )}
                {membership?.location_id && (
                  <>
                    <div className="label">
                      <div className="details-display">
                        <div>Home Location</div>
                        {(isSuperAdmin(resolvedRole) ||
                          (showChangeHomeLocationParam &&
                            (isAdmin(resolvedRole) ||
                              isGss(resolvedRole)))) &&
                          membership?.status !==
                            MEMBERSHIP_STATUS.isAwaiting && (
                            <div
                              className="blueLink text-center"
                              onClick={() => handleChangeHomeLocation()}
                            >
                              Change
                            </div>
                          )}
                      </div>
                    </div>
                    <div>{showHomeLocation()}</div>
                  </>
                )}
                <div className="label">
                  <div className="details-display">
                    <div>Squeeze Bucks</div>
                    <div
                      className="blueLink text-center"
                      onClick={() => handleWalletUpdate()}
                    >
                      +/-
                    </div>
                  </div>
                </div>
                <div>{`$${user?.balance ? user.balance : 0}`}</div>
                <div className="label">
                  <div>Preferences</div>
                </div>
                <Link
                  className="blueLink"
                  to={`/guest/preferences/${id}`}
                  query={user}
                  style={{ color: 'white' }}
                >
                  <button style={preferenceBtn}>View</button>
                </Link>
                <div className="flex guest-notes-main-wrap">
                  <Edit
                    resource={'guest'}
                    basePath={`/guest`}
                    id={id}
                    initialValues={{
                      heard_about_us: 'Select',
                    }}
                    title={' '}
                    undoable={false}
                    {...props}
                  >
                    <SimpleForm
                      toolbar={<EditToolbar />}
                      className="guest-notes-wrap"
                    >
                      <TextInput
                        label="Guest Notes"
                        source="guest_notes"
                        className="w-max guest-notes"
                        multiline
                      />
                    </SimpleForm>
                  </Edit>
                </div>
              </div>
            </SimpleShowLayout>
          </Show>
        </div>
      </div>
      <Show title={<NoTitle />} {...props} actions={<TopToolbar />}>
        <SimpleShowLayout>
          <AppBar color="transparent" position="static" className="biggerTabs">
            <Tabs
              value={appointmentIndex}
              onChange={handleChange}
              aria-label="Appointments Bar"
            >
              <Tab label="Upcoming Appointments" {...a11yProps(0)} />
              <Tab label="Completed Appointments" {...a11yProps(1)} />
              <Tab label="Cancelled Appointments" {...a11yProps(2)} />
            </Tabs>
          </AppBar>

          <TabPanel value={appointmentIndex} index={0}>
            <UserUpcomingAppointment card={GUESTS} {...props} />
          </TabPanel>
          <TabPanel value={appointmentIndex} index={1}>
            <UserCompletedAppointment card={GUESTS} {...props} />
          </TabPanel>
          <TabPanel value={appointmentIndex} index={2}>
            <UserCancelledAppointment card={GUESTS} {...props} />
          </TabPanel>
        </SimpleShowLayout>
      </Show>
      <Show title={<NoTitle />} {...props} actions={<TopToolbar />}>
        <SimpleShowLayout>
          <AppBar color="transparent" position="static" className="biggerTabs">
            <Tabs value={0} aria-label="Reviews">
              <Tab label="Reviews" {...a11yProps(0)} />
            </Tabs>
          </AppBar>
          <TabPanel value={0} index={0}>
            <UserReviews card={GUESTS} {...props} />
          </TabPanel>
        </SimpleShowLayout>
      </Show>
      <Show title={<NoTitle />} {...props} actions={<TopToolbar />}>
        <SimpleShowLayout>
          <AppBar color="transparent" position="static" className="biggerTabs">
            <Tabs value={0} aria-label="Reviews">
              <Tab label="Tips" {...a11yProps(0)} />
            </Tabs>
          </AppBar>
          <TabPanel value={0} index={0}>
            <UserTips card={GUESTS} {...props} />
          </TabPanel>
        </SimpleShowLayout>
      </Show>
      <Show title={<NoTitle />} {...props} actions={<TopToolbar />}>
        <SimpleShowLayout>
          <AppBar color="transparent" position="static" className="biggerTabs">
            <Tabs
              value={transactionsIndex}
              onChange={handleTransTabChange}
              aria-label="Transactions Bar"
            >
              <Tab label="SB Change History" {...a11yProps(0)} />
              <Tab label="CC Charges" {...a11yProps(1)} />
            </Tabs>
          </AppBar>
          <TabPanel value={transactionsIndex} index={0}>
            <UserTransactions card={GUESTS} {...props} />
          </TabPanel>
          <TabPanel value={transactionsIndex} index={1}>
            <SimpleForm
              className="styled-form2"
              toolbar={<Toolbar style={{ display: 'none' }} />}
              initialValues={{
                transactionsId: 'appointment',
              }}
            >
              <div className="grid-3">
                <SelectInput
                  source="transactionsId"
                  choices={TransactionTypes}
                  label=""
                  onChange={e => setTransactionType(e.target.value)}
                />
              </div>
            </SimpleForm>
            <UserTransactionsHistory
              card={GUESTS}
              entity_type={transactionType}
              {...props}
            />
          </TabPanel>
        </SimpleShowLayout>
      </Show>
      <Show title={<NoTitle />} {...props} actions={<TopToolbar />}>
        <SimpleShowLayout>
          <AppBar color="transparent" position="static" className="biggerTabs">
            <Tabs value={0} aria-label="Reviews">
              <Tab label="FL Gov't Required Info" {...a11yProps(0)} />
            </Tabs>
          </AppBar>
          <TabPanel value={0} index={0}>
            <SimpleForm
              className="simpleform-details-display legal-form"
              toolbar={<LegalInfoEditToolbar />}
              initialValues={{
                legal_first_name: legalInfo?.legal_first_name,
                legal_last_name: legalInfo?.legal_last_name,
                address: legalInfo?.address,
                city: legalInfo?.city,
                state: legalInfo?.state,
                zipcode: legalInfo?.zipcode,
              }}
            >
              <TextInput
                source="legal_first_name"
                label="Legal First Name"
                className="grid-one"
                validate={validateFirstName}
              />
              <TextInput
                source="legal_last_name"
                label="Legal Last Name"
                className="grid-one"
                validate={validateLastName}
              />
              <TextInput
                source="address"
                label="Address"
                className="grid-one"
                validate={validateAddress}
              />
              <TextInput
                source="city"
                label="City"
                className="grid-one"
                validate={validateCity}
              />
              <div style={{ width: '100%', padding: 0 }}>
                <SelectInput
                  source="state"
                  choices={STATES}
                  optionText={record => `${record.label}`}
                  optionValue="value"
                  label="State"
                  className="grid-one"
                  validate={validateState}
                  fullWidth
                />
              </div>

              <TextInput
                source="zipcode"
                label="Zip Code"
                className="grid-one"
                validate={validateZipCode}
                options={{
                  inputProps: {
                    inputMode: 'numeric',
                    autoComplete: 'postal-code',
                    maxLength: 5,
                  },
                }}
              />
            </SimpleForm>
          </TabPanel>
        </SimpleShowLayout>
      </Show>
    </>
  );
};
